<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div
      class="px-6 py-5 flex flex-col gap-4 max-h-[calc(100vh-250px)] overflow-y-auto"
    >
      <input-v2
        v-model.trim="name"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.LABEL')"
        :placeholder="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.PLACEHOLDER')"
        :error="nameError"
        @input="$v.name.$touch"
      />

      <input-v2
        v-model.trim="url"
        name="url"
        :dir="url ? 'ltr' : null"
        input-class="rtl:text-right"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.LABEL')"
        :placeholder="webhookURLInputPlaceholder"
        :error="urlError"
        @input="$v.url.$touch"
      />

      <dropdown-v2
        v-model="selectedInboxIds"
        class="mt-2"
        multiple
        searchable
        :options="inboxOptions"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOXES.LABEL')"
        :hint="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOXES.HINT')"
        :placeholder="
          $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOXES.PLACEHOLDER')
        "
        :search-placeholder="$t('INTEGRATION_SETTINGS.LIST.SEARCH_PLACEHOLDER')"
        :empty-text="$t('INTEGRATION_SETTINGS.LIST.NO_RESULTS')"
      />

      <div class="h-px bg-gray-200 shrink-0 my-[7px]" />

      <div class="flex flex-row items-center justify-between gap-3">
        <span class="text-sm leading-5 font-semibold text-[#000]">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.LABEL') }}
        </span>
        <button-v2
          variant="ghost"
          size="sm"
          class="!text-gray-700"
          @click="toggleSelectAll"
        >
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.SELECT_ALL') }}
        </button-v2>
      </div>

      <webhook-event-groups
        v-model="subscriptions"
        :events="supportedWebhookEvents"
        :initial-open-group="initialOpenGroup"
      />
    </div>

    <modal-footer-v2
      :cancel-text="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
      :confirm-text="submitLabel"
      confirm-type="submit"
      :is-disabled="$v.$invalid || isSubmitting"
      :is-loading="isSubmitting"
      @cancel="$emit('cancel')"
    />
  </form>
</template>

<script>
import { required, url, minLength, maxLength } from 'vuelidate/lib/validators';
import webhookMixin from './webhookMixin';
import wootConstants from 'dashboard/constants/globals';
import { mapGetters } from 'vuex';
import WebhookEventGroups from './WebhookEventGroups.vue';
import { WEBHOOK_EVENT_GROUPS } from '../OnlineCubedView/constants';

const { EXAMPLE_WEBHOOK_URL } = wootConstants;

export const SUPPORTED_WEBHOOK_EVENTS = WEBHOOK_EVENT_GROUPS.flatMap(
  group => group.events
);

export default {
  components: { WebhookEventGroups },
  mixins: [webhookMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      default: 'create',
    },
  },
  validations: {
    url: {
      required,
      minLength: minLength(7),
      url,
    },
    subscriptions: {
      required,
    },
    name: {
      maxLength: maxLength(120),
    },
  },
  data() {
    return {
      url: this.value.url || '',
      name: this.value.name || '',
      subscriptions: this.value.subscriptions || [],
      inboxes: this.alreadySelected(this.value.inbox_ids),
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
    }),
    inboxOptions() {
      return this.$store.getters['inboxes/getInboxes'].map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      }));
    },
    selectedInboxIds: {
      get() {
        return this.inboxes.map(inbox => inbox.id);
      },
      set(ids) {
        this.inboxes = this.alreadySelected(ids);
      },
    },
    webhookURLInputPlaceholder() {
      return this.$t(
        'INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.PLACEHOLDER',
        {
          webhookExampleURL: EXAMPLE_WEBHOOK_URL,
        }
      );
    },
    account() {
      return this.getAccount(this.accountId);
    },
    showDocalIntegration() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    supportedWebhookEvents() {
      let events = [...SUPPORTED_WEBHOOK_EVENTS];
      if (!this.showDocalIntegration) {
        events = events.filter(e => e !== 'signing_link_updated');
      }
      return events;
    },
    initialOpenGroup() {
      return this.mode === 'create' ? 'conversations' : null;
    },
    nameError() {
      return this.$v.name.$error
        ? this.$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.ERROR', {
            maxSize: 120,
          })
        : '';
    },
    urlError() {
      return this.$v.url.$error
        ? this.$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.ERROR')
        : '';
    },
  },
  methods: {
    alreadySelected(selectedInboxIds) {
      if (!selectedInboxIds || selectedInboxIds.length === 0) {
        return [];
      }

      const allInboxes = this.$store.getters['inboxes/getInboxes'];
      return allInboxes.filter(inbox => selectedInboxIds.includes(inbox.id));
    },
    toggleSelectAll() {
      const isAllSelected = this.supportedWebhookEvents.every(event =>
        this.subscriptions.includes(event)
      );
      const unlisted = this.subscriptions.filter(
        event => !this.supportedWebhookEvents.includes(event)
      );
      this.subscriptions = isAllSelected
        ? unlisted
        : [...unlisted, ...this.supportedWebhookEvents];
    },
    onSubmit() {
      this.$emit('submit', {
        url: this.url,
        name: this.name,
        subscriptions: this.showDocalIntegration
          ? this.subscriptions
          : this.subscriptions.filter(
              event => event !== 'signing_link_updated'
            ),
        inbox_ids: this.inboxes?.map(inbox => inbox.id),
      });
    },
  },
};
</script>
